import { useNavigate } from 'react-router-dom';

// Wave 1 — fires ~0.3s after modal slides in
const BURST_WAVE_1 = [
  { angle: -90, dist: 85,  size: 7, color: '#C89F3C', delay: 0.30, rect: false },
  { angle: -65, dist: 105, size: 5, color: '#F5D478', delay: 0.34, rect: true  },
  { angle: -45, dist: 95,  size: 6, color: '#FFE066', delay: 0.32, rect: false },
  { angle: -25, dist: 115, size: 4, color: '#D4AC4B', delay: 0.36, rect: false },
  { angle: -10, dist: 88,  size: 5, color: '#C89F3C', delay: 0.31, rect: true  },
  { angle:   5, dist: 100, size: 7, color: '#F5D478', delay: 0.35, rect: false },
  { angle:  20, dist: 82,  size: 4, color: '#FFE066', delay: 0.33, rect: false },
  { angle:  40, dist: 108, size: 6, color: '#C89F3C', delay: 0.37, rect: true  },
  { angle:  60, dist: 92,  size: 5, color: '#D4AC4B', delay: 0.32, rect: false },
  { angle:  75, dist: 98,  size: 7, color: '#F5D478', delay: 0.34, rect: false },
  { angle:  90, dist: 80,  size: 4, color: '#C89F3C', delay: 0.36, rect: true  },
  { angle: -110, dist: 72, size: 5, color: '#FFE066', delay: 0.38, rect: false },
  { angle:  110, dist: 78, size: 6, color: '#D4AC4B', delay: 0.33, rect: false },
  { angle: -130, dist: 62, size: 4, color: '#F5D478', delay: 0.35, rect: true  },
  { angle:  130, dist: 68, size: 5, color: '#C89F3C', delay: 0.31, rect: false },
];

// Wave 2 — fires ~0.75s after modal slides in, slightly different spread
const BURST_WAVE_2 = [
  { angle: -80, dist: 95,  size: 5, color: '#FFE066', delay: 0.75, rect: false },
  { angle: -55, dist: 115, size: 7, color: '#C89F3C', delay: 0.78, rect: true  },
  { angle: -35, dist: 88,  size: 4, color: '#F5D478', delay: 0.76, rect: false },
  { angle: -15, dist: 105, size: 6, color: '#D4AC4B', delay: 0.80, rect: false },
  { angle:   0, dist: 90,  size: 5, color: '#FFE066', delay: 0.77, rect: true  },
  { angle:  15, dist: 112, size: 7, color: '#C89F3C', delay: 0.79, rect: false },
  { angle:  35, dist: 85,  size: 4, color: '#F5D478', delay: 0.76, rect: false },
  { angle:  55, dist: 100, size: 6, color: '#D4AC4B', delay: 0.81, rect: true  },
  { angle:  80, dist: 92,  size: 5, color: '#FFE066', delay: 0.78, rect: false },
  { angle: -100, dist: 78, size: 6, color: '#C89F3C', delay: 0.80, rect: false },
  { angle:  100, dist: 82, size: 4, color: '#F5D478', delay: 0.77, rect: true  },
  { angle: -120, dist: 65, size: 7, color: '#D4AC4B', delay: 0.82, rect: false },
  { angle:  120, dist: 70, size: 5, color: '#FFE066', delay: 0.79, rect: false },
];

const TradeCompleteModal = ({ isOpen, counterpartyName = null, tradeId = null, onClose }) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleFeedback = () => {
    onClose();
    navigate(tradeId ? `/trade-progress/${tradeId}?scroll=feedback` : '/completed-trades');
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Trade completed"
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.52)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
        fontFamily: 'Nunito, sans-serif',
        animation: 'tcm-overlay-in 0.25s ease forwards',
      }}
      onClick={onClose}
    >
      <style>{`
        @keyframes tcm-overlay-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes tcm-sheet-in {
          from { opacity: 0; transform: translateY(36px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)    scale(1);    }
        }
        @keyframes tcm-burst {
          0%   { opacity: 1; transform: translate(0, 0) scale(1); }
          60%  { opacity: 0.9; }
          100% { opacity: 0; transform: translate(var(--tx), var(--ty)) scale(0.2); }
        }
        .tcm-sheet {
          background: #fff;
          border-radius: 25px;
          width: 100%;
          max-width: 480px;
          box-shadow: 0 8px 40px rgba(0,0,0,0.18);
          display: flex;
          flex-direction: column;
          overflow: visible;
          position: relative;
          animation: tcm-sheet-in 0.35s cubic-bezier(0.34, 1.4, 0.64, 1) forwards;
        }
        .tcm-inner {
          border-radius: 25px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }
        .tcm-burst-origin {
          position: absolute;
          top: 28px;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 0;
          pointer-events: none;
          z-index: 10;
        }
        .tcm-particle {
          position: absolute;
          top: 0; left: 0;
          animation: tcm-burst 1.1s ease-out forwards;
        }
        .tcm-header {
          background: #ffffff;
          border-bottom: 1px solid #e5e7eb;
          padding: 22px 24px 18px;
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 1rem;
        }
        .tcm-title {
          margin: 0;
          font-family: Nunito, sans-serif;
          font-size: 1.75rem;
          font-weight: 800;
          color: #C89F3C;
          line-height: 1.2;
        }
        .tcm-body {
          padding: 22px 24px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .tcm-line {
          font-family: Nunito, sans-serif;
          font-size: 1.03rem;
          font-weight: 600;
          color: #374151;
          line-height: 1.55;
          margin: 0;
        }
        .tcm-line-muted {
          font-family: Nunito, sans-serif;
          font-size: 0.97rem;
          font-weight: 600;
          color: #6B7280;
          line-height: 1.5;
          margin: 0;
        }
        .tcm-cta-wrap {
          padding: 4px 24px 24px;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .tcm-dismiss {
          background: none;
          border: none;
          cursor: pointer;
          font-family: Nunito, sans-serif;
          font-size: 0.9rem;
          font-weight: 600;
          color: #9CA3AF;
          text-align: center;
          padding: 4px 0;
        }
        @media (max-width: 768px) {
          .tcm-sheet {
            border-radius: 18px;
            max-width: 100%;
          }
          .tcm-inner {
            border-radius: 18px;
          }
          .tcm-header {
            padding: 16px 18px 14px;
          }
          .tcm-title {
            font-size: 1.5rem;
          }
          .tcm-body {
            padding: 16px 18px;
            gap: 10px;
          }
          .tcm-line {
            font-size: 0.97rem;
          }
          .tcm-line-muted {
            font-size: 0.92rem;
          }
          .tcm-cta-wrap {
            padding: 4px 18px 20px;
          }
        }
      `}</style>

      <div className="tcm-sheet" onClick={(e) => e.stopPropagation()}>

        {/* ── Gold burst particles (two waves) ────────────────────────── */}
        <div className="tcm-burst-origin">
          {[...BURST_WAVE_1, ...BURST_WAVE_2].map((p, i) => {
            const rad = (p.angle * Math.PI) / 180;
            const tx = Math.round(Math.cos(rad) * p.dist);
            const ty = Math.round(Math.sin(rad) * p.dist);
            return (
              <span
                key={i}
                className="tcm-particle"
                style={{
                  width: p.size,
                  height: p.rect ? p.size * 1.8 : p.size,
                  borderRadius: p.rect ? '2px' : '50%',
                  backgroundColor: p.color,
                  marginLeft: -p.size / 2,
                  marginTop: -p.size / 2,
                  animationDelay: `${p.delay}s`,
                  '--tx': `${tx}px`,
                  '--ty': `${ty}px`,
                }}
              />
            );
          })}
        </div>

        <div className="tcm-inner">
          {/* ── Header ────────────────────────────────────────────────── */}
          <div className="tcm-header">
            <h2 className="tcm-title">Congrats on completing your trade!</h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '22px',
                lineHeight: 1,
                color: '#9CA3AF',
                padding: '2px 4px',
                flexShrink: 0,
                fontFamily: 'Nunito, sans-serif',
              }}
            >
              ×
            </button>
          </div>

          {/* ── Body ──────────────────────────────────────────────────── */}
          <div className="tcm-body">
            <p className="tcm-line">Time to go jam on your new pedal!</p>
            <p className="tcm-line-muted">
              And the best part? You can always relist your pedals when you're ready for a new sound.
            </p>
            {counterpartyName && (
              <p className="tcm-line">
                Make sure to let <strong>{counterpartyName}</strong> know how they did.
              </p>
            )}
          </div>

          {/* ── CTA ───────────────────────────────────────────────────── */}
          <div className="tcm-cta-wrap">
            <button
              type="button"
              onClick={handleFeedback}
              style={{
                display: 'block',
                width: '100%',
                padding: '14px',
                borderRadius: '9999px',
                border: 'none',
                backgroundColor: '#C89F3C',
                color: '#ffffff',
                fontFamily: 'Nunito, sans-serif',
                fontSize: '1.05rem',
                fontWeight: 800,
                cursor: 'pointer',
                letterSpacing: '0.01em',
              }}
            >
              Leave Feedback
            </button>
            <button type="button" className="tcm-dismiss" onClick={onClose}>
              Maybe later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TradeCompleteModal;
